/*
 * Shared product-carousel engine.
 *
 * Extracted verbatim (behaviour-preserving) from sections/product-carousel.liquid's
 * inline {% javascript %} loop/tab/panel/arrow/measure engine so BOTH the homepage
 * product carousel AND the PDP Tagalys "Product Row" can consume one tested engine
 * (orch_TAGALYS_RECOMMENDATIONS §1.1 / Phase 0). Loaded ONCE, defer, in
 * layout/theme.liquid — never per-section (invariant 8, mirrors focus-trap.js /
 * tagalys-sdk-loader.js).
 *
 *   var engine = window.ProductCarouselEngine(sectionEl, options);
 *
 * options:
 *   prefix    {string}   selector/attribute prefix. Default 'data-pc' (homepage
 *                        carousel); the recommendations row passes 'data-pr'.
 *   populate  {function} optional async per-tab fill hook: populate(blockId, done).
 *                        When present the engine calls it on init and on every
 *                        setTab(); the consumer fetches + injects the track's
 *                        slides, then calls done() — the engine (re)measures/clones
 *                        and (re)builds the loop AFTER done() fires, so it never
 *                        measures/clones an empty track (L-TR-1 / §5.3). When absent
 *                        (homepage carousel) slides are server-rendered and setTab
 *                        toggles per-block visibility, unchanged.
 *
 * Returns { init, setTab, rebuild, destroy }.
 *
 * The clone-based infinite loop (desktop bidirectional + mobile/tablet oneWay),
 * D22 tab-wrap lock, and syncPanel gutter overhang are ported unchanged (FAILS §10 —
 * not adapted during the lift). The ONLY intentional behaviour change vs. the
 * original inline engine is the accessibility fix (TECH-DEBT #4): the desktop panel
 * show/hide uses removeAttribute('aria-hidden') paired with inert, never the
 * forbidden explicit-false form (invariant 6).
 */
(function () {
  if (window.ProductCarouselEngine) return;

  function ProductCarouselEngine(section, options) {
    options = options || {};
    var prefix   = options.prefix || 'data-pc';
    var populate = typeof options.populate === 'function' ? options.populate : null;
    var multiValueBlocks = !!options.multiValueBlocks;
    var allBlockId       = options.allBlockId;
    var desktopClonePeriods = options.desktopClonePeriods > 0 ? options.desktopClonePeriods : 1;

    function attr(suffix) { return prefix + '-' + suffix; }
    function sel(suffix)  { return '[' + prefix + '-' + suffix + ']'; }
    var slideSel  = sel('slide');
    var cloneAttr = attr('clone');

    var track     = section.querySelector(sel('track'));
    var panel     = section.querySelector(sel('panel'));
    var arrowsEl  = section.querySelector(sel('arrows'));
    var prevBtn   = section.querySelector(sel('prev'));
    var nextBtn   = section.querySelector(sel('next'));
    var allSlides = track ? Array.from(track.querySelectorAll(slideSel)) : [];
    var allTabs   = Array.from(section.querySelectorAll(sel('tab')));
    var tabLists  = Array.from(section.querySelectorAll(sel('tabs')));
    var desktopMq = window.matchMedia('(width >= 1024px)');
    var carouselInner = section.querySelector(sel('carousel-inner'));

    if (!track) return { init: function () {}, setTab: function () {}, rebuild: function () {}, destroy: function () {} };

    var ONE_WAY_LOOP_MAX_WIDTH = 1024;  // <1024px uses the forward-only (oneWay) loop; >= bidirectional

    var looping     = false;  // clone-based infinite loop is active for the current visible set
    var oneWay      = false;  // mobile/tablet forward-only loop mode — set fresh by buildLoop()
    var loopWidth   = 0;      // px of one full set of visible slides — the repeating period (desktop)
    var slideStride = 0;      // px a single slide advances (slide width + gap)
    var slideCount  = 0;      // number of visible real slides (one period)
    var basePos     = 0;      // scrollLeft that puts the first real slide flush-left (desktop band anchor)
    var realPeriodCount = 0;  // visible real slides per period (oneWay)
    var periodWidth     = 0;  // px width of one full period, for the oneWay wrap reposition

    // Excludes clones — makeClone() deep-clones a real slide via cloneNode(true),
    // which copies its data-pc-slide attribute along with everything else, so a
    // clone matches slideSel too. Without this filter, refreshSlides() (called
    // from rebuild(), before buildLoop()'s own clearClones() removes them) can
    // capture a PRIOR build's now-stale clones into allSlides — detached nodes
    // that return zero/garbage geometry from getBoundingClientRect() and corrupt
    // every later measurement. Only surfaces when rebuild() runs more than once
    // (multiple async/Tagalys-fallback tabs on the same carousel), since the
    // server-rendered path never calls rebuild() at all.
    function refreshSlides() {
      allSlides = track ? Array.from(track.querySelectorAll(slideSel)).filter(function (s) {
        return !s.hasAttribute(cloneAttr);
      }) : [];
    }

    // Lock each tab's minWidth to Math.max(regular, italic) so the italic hover
    // never resizes the box (the blink/twitch), mirroring flavour-category.liquid.
    // The two tab sets lock at different times because their final width settles
    // at different times: the mobile/tablet row is hug-content and not in a
    // JS-sized container (final width at init) → lock here; the desktop panel
    // tabs are max-w-full inside the syncPanel()-sized panel, so
    // locking at init would read the uncapped width and could beat max-width and
    // overflow the card (breaking D22) — they lock from syncPanel() instead
    // (lockDesktopTabWidths()).
    var mobileTabs  = Array.from(section.querySelectorAll('[' + attr('tabs') + '="mobile"] ' + sel('tab')));
    var desktopTabs = Array.from(section.querySelectorAll('[' + attr('tabs') + '="desktop"] ' + sel('tab')));
    mobileTabs.forEach(function (tab) {
      var normal = tab.offsetWidth;
      tab.style.fontStyle = 'italic';
      var italic = tab.offsetWidth;
      tab.style.fontStyle = '';
      tab.style.minWidth = Math.max(normal, italic) + 'px';
    });

    function syncPanel() {
      if (!panel || !desktopMq.matches) {
        if (panel) {
          panel.style.left  = '';
          panel.style.width = '';
        }
        return;
      }
      var slides = visibleSlides();
      if (slides.length < 3) return;
      // Measured via getBoundingClientRect (viewport-relative), not offsetLeft: with lead
      // clones prepended to the track, a real slide's offsetLeft is pushed one loop period
      // to the right, but at rest the track is scrolled by that same period (basePos), so
      // the bounding rect — which reflects the current scroll position — lands correctly.
      var innerRect  = carouselInner.getBoundingClientRect();
      var anchorRect = slides[2].getBoundingClientRect();
      var prevRect   = slides[1].getBoundingClientRect();
      // Over-cover one gutter on each side of the 3rd card so no card edge or
      // sub-pixel border peeks past the panel (Figma node 6236:34 — the panel
      // is one gutter wider than the card on both sides).
      var gutter = Math.max(0, anchorRect.left - prevRect.right);
      panel.style.left  = (anchorRect.left - innerRect.left - gutter) + 'px';
      panel.style.width = (anchorRect.width + 2 * gutter) + 'px';

      // Now the panel's real width is applied — lock the desktop tabs' min-width
      // so the italic hover never resizes the box (the twitch). Re-runs on every
      // syncPanel() (init rAF, setTab, resize, mq change), staying in sync with
      // the panel width across breakpoints.
      lockDesktopTabWidths();
    }

    // Measure-and-lock the desktop tabs AFTER syncPanel() has sized the panel.
    // Reset min-width first so offsetWidth reflects the current panel-capped
    // width, not a previously-locked value. A short label locks to its italic
    // single-line width (fit-content, matching Figma); a wrapped long label pegs
    // to the max-width cap in both font styles, so the lock equals the panel
    // width and cannot force overflow (D22 preserved).
    function lockDesktopTabWidths() {
      if (!desktopMq.matches) return;
      desktopTabs.forEach(function (tab) {
        tab.style.minWidth = '';
        var normal = tab.offsetWidth;
        tab.style.fontStyle = 'italic';
        var italic = tab.offsetWidth;
        tab.style.fontStyle = '';
        tab.style.minWidth = Math.max(normal, italic) + 'px';
      });
    }

    function visibleSlides() {
      return allSlides.filter(function (s) { return !s.hidden; });
    }

    function firstVisibleReal() {
      for (var i = 0; i < allSlides.length; i++) {
        if (!allSlides[i].hidden) return allSlides[i];
      }
      return null;
    }

    // scrollLeft that brings `el` flush to its snap start. Scroll-invariant: the
    // current scrollLeft is added back in, so the result is the same at any scroll offset.
    function flushLeftFor(el) {
      var trackRect = track.getBoundingClientRect();
      var rect      = el.getBoundingClientRect();
      var snapPad   = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      return track.scrollLeft + (rect.left - trackRect.left) - snapPad;
    }

    var focusableSelector = [
      'a[href]',
      'area[href]',
      'button',
      'iframe',
      '[tabindex]',
      '[contenteditable="true"]'
    ].join(', ');

    function makeClone(slide, role) {
      var clone = slide.cloneNode(true);
      clone.setAttribute(cloneAttr, role);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('inert');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach(function (element) {
        element.removeAttribute('id');
      });
      clone.querySelectorAll(focusableSelector).forEach(function (element) {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    function clearClones() {
      track.querySelectorAll('[' + cloneAttr + ']').forEach(function (c) { c.remove(); });
    }

    function measure() {
      var real = firstVisibleReal();
      if (!real) {
        loopWidth = 0; slideStride = 0; slideCount = 0; basePos = 0;
        periodWidth = 0; realPeriodCount = 0;
        return;
      }
      // Measure with sub-pixel rects, not rounded offsetLeft. Slide widths are fractional
      // (calc(5/6 * 100%)); rect.left differences are scroll-invariant, so period/stride
      // stay exact and the reposition can land dead-on a snap point.
      var vis   = visibleSlides();
      var realX = real.getBoundingClientRect().left;
      slideStride     = vis.length > 1 ? (vis[1].getBoundingClientRect().left - realX) : real.getBoundingClientRect().width;
      slideCount      = vis.length;
      realPeriodCount = vis.length;

      if (oneWay) {
        // Forward-only: one period is the gap to the first trail clone; the loop
        // rests at scrollLeft 0 (the ::before spacer supplies the initial peek).
        var trailClone = track.querySelector('[' + cloneAttr + '="trail"]');
        periodWidth = trailClone ? (trailClone.getBoundingClientRect().left - realX) : slideStride * slideCount;
        basePos     = 0;
      } else {
        // Desktop bidirectional: home the first real slide flush-left past the
        // prepended lead clones (unchanged).
        var lead = track.querySelector('[' + cloneAttr + '="lead"]');
        if (lead) {
          loopWidth = realX - lead.getBoundingClientRect().left;
          basePos   = flushLeftFor(real);
        } else {
          loopWidth = slideStride * slideCount;
          basePos   = 0;
        }
      }
    }

    function stopAnimation() {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf   = null;
      isAnimating = false;
      track.style.scrollSnapType = '';
    }

    // Reposition onto the equivalent real slide, landing on its EXACT snap point. The
    // target comes from the slide index, not the browser-rounded scrollLeft, so it is a
    // precise snap position and the browser has nothing to re-snap — that stray re-snap is
    // what made the wrap look fast/buggy. The shift is a whole number of periods (a full
    // clone set sits on each side), so it stays pixel-identical and invisible.
    function snapToNearestReal() {
      if (!looping || oneWay || loopWidth <= 0 || slideStride <= 0 || slideCount <= 0) return;
      var k      = Math.round((track.scrollLeft - basePos) / slideStride);
      var realK  = ((k % slideCount) + slideCount) % slideCount;
      var target = basePos + realK * slideStride;
      if (Math.abs(target - track.scrollLeft) > 0.5) track.scrollLeft = target;
    }

    // Track items in DOM order: clones plus visible (non-hidden) real slides.
    function trackItems() {
      return Array.from(track.children).filter(function (el) {
        return el.hasAttribute(cloneAttr) ||
          (el.hasAttribute(attr('slide')) && !el.hidden);
      });
    }

    // Index of the track item whose left edge is nearest the track's left edge.
    function nearestItemIndex(items) {
      var trackRect = track.getBoundingClientRect();
      var best = 0, bestDist = Infinity;
      items.forEach(function (el, i) {
        var dist = Math.abs(el.getBoundingClientRect().left - trackRect.left);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    // scrollLeft that centres `el` in the track (mobile/tablet cards snap-centre).
    function centeredFor(el) {
      var trackRect = track.getBoundingClientRect();
      var rect      = el.getBoundingClientRect();
      return track.scrollLeft + (rect.left - trackRect.left) + rect.width / 2 - trackRect.width / 2;
    }

    // Forward-only seam reposition: once two periods deep into the trail clones,
    // jump back one whole period between pixel-identical content (snap off/on).
    function repositionSeamOneWay() {
      if (!looping || !oneWay || periodWidth <= 0 || realPeriodCount <= 0) return;
      var idx = nearestItemIndex(trackItems());
      if (idx < 2 * realPeriodCount) return;
      track.style.scrollSnapType = 'none';
      track.scrollLeft -= periodWidth;
      track.style.scrollSnapType = '';
    }

    function buildLoop() {
      stopAnimation();
      clearClones();
      looping = false;

      var vis = visibleSlides();
      if (!vis.length) { if (arrowsEl) arrowsEl.hidden = true; return; }

      // Only loop when the real content actually overflows the viewport. Below
      // that (few products) there is nothing to wrap — the ::before CSS spacer
      // supplies the lone row's initial peek and native scroll-snap handles it.
      var overflows = track.scrollWidth > track.clientWidth + 1;
      if (!overflows) {
        if (arrowsEl) arrowsEl.hidden = true;
        track.scrollTo({ left: 0, behavior: 'instant' });
        return;
      }

      // Mobile/tablet (<1024px) build a forward-only ("oneWay") loop mirroring
      // sections/media-carousel.liquid: trail-only clones, swipe-driven one-card
      // paging via step(), and a periodic seam reposition. Desktop (>=1024px)
      // keeps the bidirectional lead+trail clone loop unchanged. D26 carve-out
      // (client/PM-authorised; supersedes D23's desktop-only restriction).
      oneWay = window.innerWidth < ONE_WAY_LOOP_MAX_WIDTH;
      var trail = document.createDocumentFragment();
      var p;
      if (oneWay) {
        // Two trail periods of buffer, not one: a lone period can be over-run by
        // a fast second swipe before repositionSeamOneWay() catches up.
        for (p = 0; p < 2; p++) {
          vis.forEach(function (s) { trail.appendChild(makeClone(s, 'trail')); });
        }
        track.appendChild(trail);
      } else {
        // Desktop: desktopClonePeriods lead + desktopClonePeriods trail periods
        // (1 each, unchanged, for this engine's original two consumers).
        var lead = document.createDocumentFragment();
        for (p = 0; p < desktopClonePeriods; p++) {
          vis.forEach(function (s) { lead.appendChild(makeClone(s, 'lead')); });
          vis.forEach(function (s) { trail.appendChild(makeClone(s, 'trail')); });
        }
        track.appendChild(trail);
        track.insertBefore(lead, track.firstChild);
      }

      looping = true;
      // Arrows are a desktop-only affordance (per Figma); hidden on the oneWay
      // mobile/tablet loop, shown on desktop — same as the prior behaviour.
      if (arrowsEl) arrowsEl.hidden = oneWay;
      if (prevBtn) prevBtn.setAttribute('aria-disabled', 'false');
      if (nextBtn) nextBtn.setAttribute('aria-disabled', 'false');

      measure();
      track.scrollLeft = basePos;
    }

    function currentIndex() {
      var slides = visibleSlides();
      if (!slides.length) return 0;
      var trackLeft = track.getBoundingClientRect().left;
      var best = 0;
      var bestDist = Infinity;
      slides.forEach(function (s, i) {
        var dist = Math.abs(s.getBoundingClientRect().left - trackLeft);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    function syncArrows() {
      if (looping) return;
      if (!prevBtn || !nextBtn) return;
      var slides     = visibleSlides();
      var idx        = currentIndex();
      var last       = slides.length - 1;
      var lastSlide  = slides[last];
      var trackRight = track.getBoundingClientRect().right;
      var atEnd      = lastSlide
        ? Math.round(lastSlide.getBoundingClientRect().right) <= Math.round(trackRight) + 1
        : true;
      prevBtn.setAttribute('aria-disabled', idx === 0 ? 'true' : 'false');
      nextBtn.setAttribute('aria-disabled', (idx >= last || atEnd) ? 'true' : 'false');
    }

    var scrollRaf = null, isAnimating = false;

    function tweenDuration() {
      return Math.round((parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--duration-a7')) || 0.4) * 1000);
    }

    function easeInOut(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    function animateBy(delta, onSettle) {
      if (!delta) return;
      stopAnimation();
      track.style.scrollSnapType = 'none';
      isAnimating = true;

      var start     = track.scrollLeft;
      var duration  = tweenDuration();
      var startTime = null;

      function stepFrame(ts) {
        if (startTime === null) startTime = ts;
        var progress = Math.min((ts - startTime) / duration, 1);
        track.scrollLeft = start + delta * easeInOut(progress);
        if (progress < 1) {
          scrollRaf = requestAnimationFrame(stepFrame);
        } else {
          scrollRaf   = null;
          isAnimating = false;
          // Land on the exact seam point while snap is still off, then restore snap.
          onSettle();
          track.style.scrollSnapType = '';
        }
      }

      scrollRaf = requestAnimationFrame(stepFrame);
    }

    // Advance one card. Desktop steps a fixed stride and settles with the
    // bidirectional seam snap; oneWay centres the next track item and settles
    // with the forward-only seam reposition.
    function step(dir) {
      if (!looping) return;
      if (oneWay) {
        var items = trackItems();
        if (!items.length) return;
        var nextIdx = nearestItemIndex(items) + dir;
        if (nextIdx < 0) return;
        var nextEl = items[nextIdx];
        if (!nextEl) return;
        var target = nextIdx === 0 ? 0 : centeredFor(nextEl);
        animateBy(target - track.scrollLeft, repositionSeamOneWay);
      } else {
        if (slideStride <= 0) return;
        animateBy(dir * slideStride, snapToNearestReal);
      }
    }

    // (Re)measure + (re)clone the current track slides and rebuild the loop.
    // The async (populate) consumer calls this AFTER injecting a tab's cards so
    // the loop/panel never measure an empty track (L-TR-1 / §5.3).
    function rebuild() {
      refreshSlides();
      buildLoop();
      syncPanel();
    }

    function setTab(blockId) {
      allTabs.forEach(function (tab) {
        var isActive = tab.getAttribute(attr('tab')) === blockId;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.classList.toggle('italic',      isActive);
        tab.classList.toggle('not-italic', !isActive);
        tab.classList.toggle('opacity-100', isActive);
        tab.classList.toggle('opacity-75', !isActive);
        // Keep the inactive hover affordance in sync: the first tab renders
        // active (no hover classes), so restore them whenever a tab is inactive.
        tab.classList.toggle('hover:italic',      !isActive);
        tab.classList.toggle('hover:opacity-100', !isActive);
      });

      if (populate) {
        // Async fill: the consumer clears + injects this tab's cards, then calls
        // done() → rebuild() measures/clones the freshly-injected slides.
        populate(blockId, rebuild);
      } else {
        // Server-rendered slides: toggle per-block visibility.
        allSlides.forEach(function (slide) {
          if (multiValueBlocks) {
            var raw = slide.getAttribute(attr('block'));
            var isAllTab     = blockId === allBlockId;
            var isUniversal  = !raw;
            var matchesBlock = raw ? raw.split(',').indexOf(blockId) !== -1 : false;
            slide.hidden = !isAllTab && !isUniversal && !matchesBlock;
          } else {
            slide.hidden = slide.getAttribute(attr('block')) !== blockId;
          }
        });
        buildLoop();
        syncPanel();
      }
    }

    allTabs.forEach(function (tab) {
      tab.addEventListener('click', function () { setTab(tab.getAttribute(attr('tab'))); });
    });

    tabLists.forEach(function (tabList) {
      tabList.addEventListener('keydown', function (e) {
        // Built here, not once up front — product-carousel-script.liquid's
        // hideEmptyTab() can set tab.hidden = true after this listener is
        // wired up, and a stale snapshot would still let Arrow/Home/End land
        // on a hidden tab (focus() is a no-op there, but setTab() still
        // activates the now-empty block behind it).
        var listTabs = Array.from(tabList.querySelectorAll(sel('tab'))).filter(function (t) { return !t.hidden; });
        var idx = listTabs.indexOf(document.activeElement);
        if (idx === -1) return;
        var next;
        if      (e.key === 'ArrowRight') next = (idx + 1) % listTabs.length;
        else if (e.key === 'ArrowLeft')  next = (idx - 1 + listTabs.length) % listTabs.length;
        else if (e.key === 'Home')       next = 0;
        else if (e.key === 'End')        next = listTabs.length - 1;
        else return;
        e.preventDefault();
        listTabs[next].focus();
        setTab(listTabs[next].getAttribute(attr('tab')));
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () { step(-1); });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () { step(1); });
    }

    track.addEventListener('scroll', function () {
      if (!isAnimating) syncArrows();
    }, { passive: true });

    // Reposition the loop seam only once scrolling has fully settled — never mid-fling.
    // Setting scrollLeft during momentum cancels the fling and, with mandatory snap,
    // re-snaps over it (the fast-scroll jitter). A full set of clones sits on each side
    // (always wider than the viewport), so a fling stays on identical content until it
    // stops; the jump is then invisible (clones are pixel-identical, scrollbar hidden).
    // Prefer the native `scrollend` — it fires once momentum AND the browser's
    // snap-settle are both done; fall back to a 150ms scroll-idle debounce where
    // it isn't supported. Dispatch to the mode-correct seam function.
    var idleTimer = null;
    function repositionSeam() {
      if (isAnimating) return;
      if (oneWay) repositionSeamOneWay(); else snapToNearestReal();
    }
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', repositionSeam, { passive: true });
    } else {
      track.addEventListener('scroll', function () {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(repositionSeam, 150);
      }, { passive: true });
    }

    var touchX = 0, touchY = 0, touchDir = null;

    track.addEventListener('touchstart', function (e) {
      touchDir = null;
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (touchDir === null) {
        var dx = Math.abs(e.touches[0].clientX - touchX);
        var dy = Math.abs(e.touches[0].clientY - touchY);
        if (dx > 5 || dy > 5) touchDir = dx >= dy;
      }
      // Swipe drives the one-card JS animation (step()); only hijack native
      // scroll while a loop is actually built (overflowing content).
      if (touchDir && looping) e.preventDefault();
    }, { passive: false });

    track.addEventListener('touchend', function (e) {
      if (!touchDir || isAnimating || !looping) { touchDir = null; return; }
      var delta = e.changedTouches[0].clientX - touchX;
      if      (delta < -30) step(1);
      else if (delta >  30) step(-1);
      touchDir = null;
    }, { passive: true });

    track.addEventListener('touchcancel', function () {
      touchDir = null;
    }, { passive: true });

    // Show/hide the desktop panel WITHOUT ever writing the forbidden explicit-false
    // form (invariant 6 / TECH-DEBT #4 fix): pair aria-hidden with inert and clear
    // both when the panel is shown.
    function setPanelHidden(hidden) {
      if (!panel) return;
      if (hidden) {
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      } else {
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('inert');
      }
    }

    var firstTab = allTabs[0];

    function onMqChange() {
      buildLoop(); syncPanel();
      setPanelHidden(!desktopMq.matches);
    }
    desktopMq.addEventListener('change', onMqChange);

    // Slide widths depend on viewport WIDTH, so only rebuild when the width actually
    // changes. Ignoring height-only resizes stops the loop rebuilding/re-homing every
    // time mobile browser chrome (the URL bar) shows or hides mid-scroll.
    var lastWidth   = window.innerWidth;
    var resizeTimer = null;
    function onResize() {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { buildLoop(); syncPanel(); }, 150);
    }
    window.addEventListener('resize', onResize, { passive: true });

    function init() {
      if (firstTab) {
        setTab(firstTab.getAttribute(attr('tab')));
      } else if (populate) {
        // No tabs but an async source (defensive) — fill once then rebuild.
        populate(null, rebuild);
      } else {
        buildLoop();
        syncPanel();
      }

      requestAnimationFrame(function () {
        requestAnimationFrame(syncPanel);
      });
      setPanelHidden(!desktopMq.matches);
    }

    function destroy() {
      stopAnimation();
      clearClones();
      desktopMq.removeEventListener('change', onMqChange);
      window.removeEventListener('resize', onResize);
    }

    init();

    return { init: init, setTab: setTab, rebuild: rebuild, destroy: destroy };
  }

  window.ProductCarouselEngine = ProductCarouselEngine;
})();
